/**
 * ProviderPickerModal - Premium Provider Selection Component
 * 
 * A polished, trustworthy provider selection modal for the credit report import flow.
 * Uses official logos or premium SVG wordmarks instead of emoji icons.
 */

import React from 'react';
import { X, Shield } from 'lucide-react';
import { Button } from '../ui/button';
import { getActiveProviders } from '../../config/providerRegistry';
import ProviderSelectCard from './ProviderSelectCard';

const ProviderPickerModal = ({
  isOpen,
  onClose,
  onSelectProvider,
  title = 'Select Your Provider',
  subtitle = 'Choose your credit monitoring service to connect',
}) => {
  const providers = getActiveProviders();

  if (!isOpen) return null;

  const handleProviderSelect = (provider) => {
    onSelectProvider({
      id: provider.provider_key,
      name: provider.provider_name,
      description: provider.provider_description,
      logo: provider.LogoComponent,
      logoSrc: provider.logo_src,
      referralUrl: provider.referral_url,
      loginUrl: provider.login_url,
      badgeText: provider.badge_text,
      brandColor: provider.brand_color,
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-3 sm:p-4 bg-stone-900/60 backdrop-blur-sm overflow-hidden"
      data-testid="provider-picker-modal"
    >
      <div
        className="w-[calc(100vw-24px)] sm:w-full max-w-lg max-h-[min(90dvh,720px)] flex flex-col bg-white dark:bg-[#0B1F18] rounded-2xl shadow-2xl dark:shadow-black/40 animate-fade-in overflow-hidden border border-transparent dark:border-emerald-500/10"
        data-testid="provider-picker-content"
      >
        {/* Header */}
        <div className="shrink-0 px-4 py-4 sm:px-6 sm:py-5 border-b border-slate-100 dark:border-emerald-500/15">
          <div className="flex items-start justify-between gap-3 min-w-0">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 dark:bg-emerald-500/15 flex items-center justify-center flex-shrink-0">
                <Shield className="text-emerald-600 dark:text-emerald-400" size={20} />
              </div>
              <div className="min-w-0">
                <h2 className="text-lg font-semibold text-stone-900 dark:text-[#EAF7F0]">{title}</h2>
                <p className="text-sm text-slate-500 dark:text-[#93AE9F] mt-0.5 break-normal whitespace-normal">
                  {subtitle}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-white/5 transition-colors shrink-0"
              data-testid="close-provider-picker"
            >
              <X size={20} className="text-slate-400 dark:text-[#93AE9F]" />
            </button>
          </div>
        </div>

        {/* Provider List */}
        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden p-3 sm:p-4 pb-4 space-y-3">
          {providers.map((provider) => (
            <ProviderSelectCard
              key={provider.provider_key}
              provider={provider}
              variant="modal"
              onClick={() => handleProviderSelect(provider)}
            />
          ))}
        </div>

        {/* Footer */}
        <div className="shrink-0 px-4 py-3 sm:px-6 sm:py-4 bg-slate-50 dark:bg-[#081610] border-t border-slate-100 dark:border-emerald-500/15">
          <div className="flex items-center justify-between gap-3 min-w-0">
            <p className="text-xs text-slate-500 dark:text-[#93AE9F] break-normal whitespace-normal min-w-0">
              Secure connection via encrypted credentials
            </p>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="text-slate-500 hover:text-slate-700 dark:text-[#C7DDD0] dark:hover:text-[#EAF7F0] shrink-0"
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProviderPickerModal;
